"use client";

import { ListingSearchData } from "@/lib/types/search";
import React from "react";
import Image from "next/image";
import { useFormatter, useTranslations } from "next-intl";
import { Link } from "@/i18n/routing";
import { DoorOpen, Expand, Sofa } from "lucide-react";
import { twMerge } from "tailwind-merge";
import { DEFAULT_IMAGE } from "@/lib/data/images";
import { IMAGE_SIZES } from "@/lib/imageSizes";
import ListingPromotionBadge from "./ListingPromotionBadge";
import ListingPublisherByline from "./ListingPublisherByline";
import EnergyChip from "./EnergyChip";

type Props = {
  item: ListingSearchData;
  className?: string;
};

/**
 * Vertical listing card for carousels and showcase sections (home "latest",
 * similar listings, property-type carousels): photo on top, details below.
 * Same data contract as ListingSearchItem, different layout.
 */
const ListingCarouselItem: React.FC<Props> = ({ item, className }) => {
  const t = useTranslations("listing");
  const formatter = useFormatter();

  return (
    <Link
      href={item.href}
      title={item.title}
      className={twMerge(
        "google-anno-skip group flex h-full flex-col overflow-hidden rounded-2xl border border-border bg-background shadow-sm transition-[box-shadow,border-color] hover:border-zinc-300 hover:shadow-[0_10px_28px_-16px_rgba(24,24,27,0.45)]",
        className,
      )}
    >
      {/* max-h keeps the photo sensible when few cards stretch wide in a grid */}
      <div className="relative aspect-[16/10] max-h-56 w-full overflow-hidden">
        <Image
          src={item.imageUrl || DEFAULT_IMAGE.LISTING}
          alt={item.title}
          width={640}
          height={400}
          unoptimized
          sizes={IMAGE_SIZES.cardMedium}
          className="absolute inset-0 h-full w-full object-cover"
        />
        <ListingPromotionBadge
          promotion={item.promotion}
          className="absolute left-2 top-2"
        />
        {item.attributes.energyLabel && (
          <EnergyChip
            label={item.attributes.energyLabel}
            className="absolute right-2 top-2 shadow-sm"
          />
        )}
      </div>

      <div className="flex flex-1 flex-col gap-0.5 p-3">
        <p className="line-clamp-2 text-sm font-bold leading-snug text-blue-700">
          {item.title}
        </p>
        <ListingPublisherByline publisher={item.publisher} linked={false} />
        {item.isAvailable === false && (
          <p className="text-xs font-medium text-amber-800">
            {t("unavailable")}
          </p>
        )}
        <p className="truncate text-xs text-muted-foreground">
          {item.location.postalCode
            ? `${item.location.postalCode} · ${item.location.city}`
            : item.location.city}
        </p>
        <p className="mt-0.5 text-foreground">
          <span className="text-base font-bold">
            {formatter.number(item.attributes.price, {
              style: "currency",
              currency: "EUR",
              minimumFractionDigits: 0,
            })}
          </span>
          <span className="text-xs text-muted-foreground">
            {t("price-month")}
          </span>
        </p>
        <div className="mt-1 flex flex-row flex-wrap items-center gap-x-2.5 gap-y-1 text-[11px] text-zinc-700">
          <span className="inline-flex items-center gap-1 whitespace-nowrap">
            <Expand
              className="size-3 text-muted-foreground"
              strokeWidth={1.5}
            />
            {t("size", { size: item.attributes.size })}
          </span>
          <span className="inline-flex items-center gap-1 whitespace-nowrap">
            <DoorOpen
              className="size-3 text-muted-foreground"
              strokeWidth={1.5}
            />
            {t("rooms", { count: item.attributes.roomsCount })}
          </span>
          <span className="inline-flex items-center gap-1 whitespace-nowrap">
            <Sofa className="size-3 text-muted-foreground" strokeWidth={1.5} />
            {t(`furnished.${item.attributes.furnishedType}`)}
          </span>
        </div>
      </div>
    </Link>
  );
};

export default ListingCarouselItem;
